import { Link } from 'react-router-dom';
import type { ExecutiveDisclosureResponse, SlackConnectionResponse } from '../../shared/api-types.js';
import { AI_PROVIDER_LABELS, FELLOW_MODE_LABELS, SUBJECT_PROVIDER_LABELS, type AiProvider } from '../../shared/enums.js';
import { displayVersion } from '../../shared/text.js';
import { api } from '../api.js';
import { PATHS } from '../paths.js';
import { useMe } from '../shell/me-context.js';
import { Avatar, CopyButton, FailureView, Page, PageHead, Section, StateView, browserTimeZone, timeZoneLabel, useRequest, type RequestState } from '../ui/index.js';
import { FELLOW_STATE_OUTCOME, FELLOW_STATE_PRIVACY, FELLOW_TICK_OFF_ITEMS, fellowStateTiming } from './lib/connection.js';
import { EXECUTIVE_DISCLOSURE_LEAD, climateKeptBullet, executiveDisclosureSentences, whoSeesYourDataSentence, yourDataKeepsLead } from './lib/executive-disclosure.js';
import { PROJECTS_KEPT_BULLET, PROJECT_SHARING_PARAGRAPH, aiCallsParagraph, heldSlackData, slackDisclosure, slackKeptSentence, type HeldSlackData } from './lib/slack-connection.js';
import { fellowStateOn, fellowTickOffGate } from './lib/todos.js';
import './pages.css';

// Settings (spec §13): the profile as the sign-in provider supplied it, the timezone every
// formatter uses, the running version as selectable text (app-baseline 01-identity §1.3), and a
// plain statement of what Calis stores and where transcripts — and, where Slack is switched on,
// related Slack messages — go. Where Slack has been switched off, what the person's earlier Slack
// connection left is still listed until it is deleted. Under the projects floor (ADR 0017) it also
// says what a project is, where its name goes, and what sharing one shows a colleague. Under the executive floor (ADR
// 0019; the owner's pick E13) it says what the executives' organisation report reads of the member, names who can read
// it, and counts the reports the member's meetings were in.

export default function SettingsPage() {
  const me = useMe();
  const timeZone = browserTimeZone();
  const zoneLabel = timeZoneLabel(timeZone);
  const aiLabel = AI_PROVIDER_LABELS[me.integrations.ai];
  const fellowLabel = FELLOW_MODE_LABELS[me.integrations.fellow];
  const version = displayVersion(me.version);
  const slackOn = me.integrations.slack !== 'off';
  // Why said only under the floor (owner ruling 9 of 2026-10-02): with PROJECTS_MODE off there are no projects, no
  // name leaves and nothing can be shared, so every sentence below would describe what does not happen.
  const projectsOn = me.integrations.projects === 'on';
  // Why said only under the floor (ADR 0019, stage 2, item 3; design §2.14 items 1–3): with EXECUTIVE_MODE off — or no AI
  // provider — nothing reads a member's material for anyone else, so "for your account only" and "Only you can see"
  // stand as they were.
  const executiveOn = me.integrations.executive === 'on';
  const sweepOn = me.integrations.slackSweep === 'on';
  // Why the reader's own permissions (the owner's ruling of 2026-10-13 on ADR 0019's Open 56, "Count them too"): a member
  // without AI summaries who holds both Slack permissions has their Slack climate counted into the organisation pulse, and
  // is told so in words said of them (lib/executive-disclosure.ts `whoSeesYourDataSentence`). Selection of a sentence only:
  // the executor asks the gate itself at every run.
  const holdsAiSummaries = me.capabilities.aiSummaries;
  const holdsSlackAnalysis = me.capabilities.slackConnect && me.capabilities.slackAnalysis;
  // Why asked whatever the mode (scope access only): a connection made before Slack was switched off
  // is kept until the person disconnects, and the checks it made until then or retention.
  const slack = useRequest<SlackConnectionResponse>((signal) => api.get<SlackConnectionResponse>('/api/connections/slack', { signal }), []);
  const held = slackOn ? null : heldSlackOf(slack);

  return (
    <Page measure="reading">
      <PageHead title="Settings" />

      <Section id="profile" title="Profile" description="From your sign-in provider.">
        <div className="sheet stack">
          <div className="cluster cluster--3">
            <Avatar name={me.subject.displayName} email={me.subject.email} hashKey={me.subject.id} initials={me.subject.initials} size="lg" decorative />
            <div className="stack stack--1">
              <p className="text-md"><strong>{me.subject.displayName}</strong></p>
              <p className="secondary">{me.subject.email}</p>
            </div>
          </div>
          <dl className="field-list">
            <dt>Signed in with</dt>
            <dd>{SUBJECT_PROVIDER_LABELS[me.authMode]}</dd>
            <dt>Timezone</dt>
            <dd>
              {timeZone} <span className="muted">({zoneLabel})</span>
              <span className="muted text-sm"> — from this browser</span>
            </dd>
            <dt>Version</dt>
            <dd>
              <span className="selectable">{version}</span> <CopyButton value={version} label="Copy version" />
            </dd>
            {me.subject.isSuperuser ? (
              <>
                <dt>Role</dt>
                <dd>Superuser, from the deployment&rsquo;s configuration</dd>
              </>
            ) : null}
          </dl>
        </div>
      </Section>

      <Section id="your-data" title="Your data">
        <div className="sheet settings-prose">
          <p>{yourDataKeepsLead(executiveOn)}</p>
          <ul>
            <li>your name, email address and sign-in identity as your provider reports them;</li>
            <li>the meetings imported from Fellow — titles, times, attendees, transcripts and Fellow&rsquo;s own notes;</li>
            <li>the summaries, key points, decisions and to-dos generated from them, and the to-dos you add or change;</li>
            {executiveOn ? <li>{climateKeptBullet({ slackOn, sweepOn })}</li> : null}
            {projectsOn ? <li>{PROJECTS_KEPT_BULLET}</li> : null}
            {slackOn ? (
              <>
                <li>{FELLOW_KEY_BULLET}</li>
                <li>if you connect Slack, your Slack access, encrypted at rest and never shown, the list of your Slack channels, direct messages and group chats (channel names encrypted, the people in a direct message by their Slack id), and the channels you tell Calis not to read (their names encrypted);</li>
                <li>what Calis finds when it checks a meeting against Slack — findings and short quoted excerpts of the related messages, encrypted — and how each check looked, as counts only;</li>
                <li>the to-dos and notes a Slack check writes onto your to-do list (marked <em>From Slack</em>), in plain text, kept like any to-do until you delete them — disconnecting Slack does not remove them — and a to-do&rsquo;s reason or a note may name the channel, group chat or direct message it came from, private channels included; and</li>
              </>
            ) : <SlackOffKeptItems request={slack} />}
            <li>an audit trail of changes made to your data, kept for a limited window and visible to administrators.</li>
          </ul>
          {me.integrations.ai === 'off' ? (
            <p>No AI provider is configured on this installation, so transcripts are not sent anywhere and no summaries are generated.</p>
          ) : (
            <p>
              {/* Why whole alternatives here and for Fellow and Slack below (05-content §8; the Phase 1c audit's D30): the demo's
                  parenthesis was spliced into the sentence by condition. */}
              {me.integrations.ai === 'offline'
                ? <>To generate a summary and its to-dos, Calis sends the meeting&rsquo;s transcript to the configured AI provider, <strong>{aiLabel}</strong> (a deterministic demo summariser that runs on this server and sends nothing outside it).</>
                : <>To generate a summary and its to-dos, Calis sends the meeting&rsquo;s transcript to the configured AI provider, <strong>{aiLabel}</strong>.</>}{' '}
              {/* Which of this reader's AI calls carry the names (design §3.5): built from their own grant and this installation's switches, never a fixed list. */}
              {aiCallsParagraph({ projectsOn, holdsAiSummaries: me.capabilities.aiSummaries, slackOn, sweepOn, assistantOn: me.integrations.assistant !== 'off', holdsSlackAnalysis: me.capabilities.slackConnect && me.capabilities.slackAnalysis })}
            </p>
          )}
          <p>
            {me.integrations.fellow === 'fixture'
              ? <>Meetings come from <strong>{fellowLabel}</strong> (a built-in demo dataset, not a real Fellow workspace).</>
              : <>Meetings come from <strong>{fellowLabel}</strong>.</>} {whoSeesYourDataSentence({ executiveOn, projectsOn, slackOn, sweepOn, holdsAiSummaries, holdsSlackAnalysis })} Manage the connection under{' '}
            <Link to={PATHS.connections}>Connections</Link>.
          </p>
          {/* Why here, right after "the executives named below" (design §2.13: the slot beside linked to-dos): the one place a
              member is told who reads the report their meetings feed. */}
          {executiveOn ? <ExecutiveNote holdsAiSummaries={holdsAiSummaries} /> : null}
          {me.integrations.todoLinks === 'on' ? (
            // Why here (ADR 0014; design §7): the one place Calis lets another person's action change
            // your list, said beside who can see what — and what a linked colleague learns of you.
            <p>
              When a colleague invited to the same Fellow meeting has the same to-do, ticking it off ticks off theirs too, and theirs ticks off
              yours. They never see your wording or notes: they learn only that your copy was ticked off or reopened, and when, with your name if
              you are on their own invite list. Unlink a to-do to stop it.
            </p>
          ) : null}
          {projectsOn ? (
            // Sharing a project (ADR 0017, design §8.2): the second place another person reads something of yours, said
            // beside the first (linked to-dos) — what they see, what never crosses, who ends it, and who can find you.
            <p>{PROJECT_SHARING_PARAGRAPH}</p>
          ) : null}
          <FellowTickOffNote me={me} />
          <FellowStateNote me={me} />
          {slackOn ? <SlackDataNote request={slack} ai={me.integrations.ai} fixture={me.integrations.slack === 'fixture'} climate={me.integrations.executive === 'on'} /> : null}
          {held ? (
            <p>
              {held.note} Manage it under <Link to={PATHS.connections}>Connections</Link>.
            </p>
          ) : null}
        </div>
      </Section>
    </Page>
  );
}

/** The Fellow key's bullet, as one whole sentence either way (05-content §8; the Phase 1c audit's D30). */
const FELLOW_KEY_BULLET = 'your Fellow API key, encrypted at rest and never shown — only an eight-character fingerprint of it, which is not part of the key;';
const FELLOW_KEY_BULLET_LAST_BUT_ONE = 'your Fellow API key, encrypted at rest and never shown — only an eight-character fingerprint of it, which is not part of the key; and';

type SlackRequest = Pick<RequestState<SlackConnectionResponse>, 'status' | 'data' | 'error' | 'reload'>;

/**
 * What an earlier Slack connection left, from the real answer only — never from a held answer the page could not
 * refresh (the owner's ruling 1c-OQ4 of 2026-10-07, "Withhold the list", as SlackDataNote reads it); null while it is
 * unknown and when there is no connection.
 */
export function heldSlackOf(request: SlackRequest): HeldSlackData | null {
  return request.status === 'ready' && request.data ? heldSlackData(request.data.connection, request.data.retentionDays) : null;
}

/**
 * Settings → Your data with Slack switched off: the Fellow key's bullet, then what an earlier Slack connection left.
 * Why the states (the owner's ruling 1c-OQ4 of 2026-10-07, "Withhold the list"; 03-components §1; directives §4; the
 * Phase 2a review): built before the answer arrives — or after it failed or was refused — the list fell back to "no
 * connection", ended at the Fellow key with "; and", and left out the encrypted Slack access, the findings with their
 * quoted excerpts, the climate words and the to-dos an earlier connection left: a description from a default that told
 * the person less than Calis holds of their data. So the held part says it is loading, or what failed with a retry, and
 * the Fellow bullet takes its "; and" only once the answer says nothing is held.
 */
export function SlackOffKeptItems({ request }: { request: SlackRequest }) {
  if (request.status !== 'ready' || !request.data) {
    return (
      <>
        <li>{FELLOW_KEY_BULLET}</li>
        <li data-held-slack="">
          {request.status === 'error' && request.error ? (
            <FailureView failure={request.error} what="what Calis keeps from an earlier Slack connection" onRetry={request.reload} compact />
          ) : (
            <StateView kind="loading" label="Loading what Calis keeps from an earlier Slack connection" compact minHeight="3rem" />
          )}
        </li>
      </>
    );
  }
  const held = heldSlackOf(request);
  if (!held) return <li>{FELLOW_KEY_BULLET_LAST_BUT_ONE}</li>;
  return (
    <>
      <li>{FELLOW_KEY_BULLET}</li>
      {held.items.map((item) => <li key={item}>{item}</li>)}
    </>
  );
}

/**
 * Executive intelligence, as a member is told it in Settings → Your data (the owner's pick E13; design §2.13 and §2.14
 * item 3): who may read the organisation report their meetings feed — named as members are named to each other, resolved
 * by the server at this request (`GET /api/me/executives`: `admit()` and the one gate for each holder) — in how many of
 * the last 30 days' reports their own meetings were, and the one-off re-summary. Mounted only while the executive floor is
 * on, so nothing is asked of the server otherwise.
 */
function ExecutiveNote({ holdsAiSummaries }: { holdsAiSummaries: boolean }) {
  const request = useRequest<ExecutiveDisclosureResponse>((signal) => api.get<ExecutiveDisclosureResponse>('/api/me/executives', { signal }), []);
  return <ExecutiveDisclosureNote request={request} holdsAiSummaries={holdsAiSummaries} />;
}

/**
 * The paragraph itself, from the request's state. Its run-in heading and lead are said at once; what the server resolves
 * has the states of fetched data (03-components §1) — loading after the usual delay, a refusal or failure with its own
 * words and a retry — and never an empty list: nobody able to read it is said so (`executivesSentence`). Should the
 * server say the floor is off (a restart between two reads), nothing is said: no report is written then.
 */
export function ExecutiveDisclosureNote({ request, holdsAiSummaries }: { request: Pick<RequestState<ExecutiveDisclosureResponse>, 'status' | 'data' | 'error' | 'reload'>; holdsAiSummaries: boolean }) {
  const lead = (
    <>
      <strong>Executive intelligence.</strong> {EXECUTIVE_DISCLOSURE_LEAD}
    </>
  );
  if (request.status === 'ready' && request.data) {
    const sentences = executiveDisclosureSentences(request.data, holdsAiSummaries);
    if (sentences === null) return null;
    return (
      <p data-executive-disclosure="">
        {lead} {sentences.join(' ')}
      </p>
    );
  }
  return (
    <div className="stack stack--2" data-executive-disclosure="">
      <p>{lead}</p>
      {request.status === 'error' && request.error ? (
        <FailureView failure={request.error} what="the executives’ names" onRetry={request.reload} compact />
      ) : (
        <StateView kind="loading" label="Loading the executives’ names" compact minHeight="3rem" />
      )}
    </div>
  );
}

/**
 * The one thing Calis writes to Fellow, said beside what it keeps (owner, 2026-09-30, decisions O1(b), O3(b), O5(a),
 * O6(b)): with whose key, which items, and that Fellow records the key's person as the one who did it. Why only to
 * someone it applies to — the switch on and Connect Fellow theirs: to anyone else it would describe a write Calis never
 * makes for them.
 */
export function FellowTickOffNote({ me }: { me: Parameters<typeof fellowTickOffGate>[0] }) {
  if (fellowTickOffGate(me) !== 'on') return null;
  return (
    <p>
      When you tick off a to-do that came from Fellow, Calis ticks off its action item in Fellow too, with your own Fellow
      API key — also when a colleague&rsquo;s tick reaches your linked copy — and reopens it there if you reopen it here
      after Calis ticked it off. {FELLOW_TICK_OFF_ITEMS} Calis sends Fellow only that the item is done or open again, and
      Fellow shows the account behind your key as the one who did it.
    </p>
  );
}

/**
 * What Calis reads back from Fellow (Stage 3, owner decision O4(a); design §7.2), said beside what it keeps: the
 * person's action-item list, read with their own key, of which only the items Calis already holds for them are kept —
 * and what a change made there does here. Why only to someone it applies to — the switch on and Connect Fellow theirs:
 * to anyone else it would describe a read Calis never makes for them.
 */
export function FellowStateNote({ me }: { me: Parameters<typeof fellowStateOn>[0] }) {
  if (!fellowStateOn(me)) return null;
  return (
    <p>
      {fellowStateTiming(null)} {FELLOW_STATE_OUTCOME} {FELLOW_STATE_PRIVACY}
    </p>
  );
}

/**
 * What a Slack check reads, how it looks, what it sends and what it keeps — the same facts the
 * Connections page states before anyone connects (owner ruling 20260926-002133, amended, R7 and R8;
 * ADR 0010), in the same labelled shape, with this installation's settings.
 *
 * Why the list waits for the settings (03-components §1; the owner's ruling 1c-OQ4 of 2026-10-07, "Withhold the list";
 * Phase 1c finding D15): built before they arrive, it would fall back to defaults — the daily sweep off — and tell the
 * person less than Calis does with their data (no daily read, nothing sent to Anthropic daily). So while the settings
 * load the note says it is loading, a failure or a refusal says so with its retry, and only the real settings make the
 * list; the lead sentence, which no setting changes, stands throughout.
 */
export function SlackDataNote({ request, ai, fixture, climate }: { request: Pick<RequestState<SlackConnectionResponse>, 'status' | 'data' | 'error' | 'reload'>; ai: AiProvider; fixture: boolean; climate: boolean }) {
  const lead = <p>{fixture ? 'If you connect Slack (here, a built-in demo workspace, not a real one), Calis checks your meetings against it.' : 'If you connect Slack, Calis checks your meetings against it.'}</p>;
  const settings = request.status === 'ready' ? request.data : null;
  if (!settings) {
    return (
      <div className="stack stack--2" data-slack-disclosure="">
        {lead}
        {request.status === 'error' && request.error ? (
          <FailureView failure={request.error} what="what Calis reads from Slack for you" onRetry={request.reload} compact />
        ) : (
          <StateView kind="loading" label="Loading what Calis reads from Slack for you" compact minHeight="3rem" />
        )}
      </div>
    );
  }
  const disclosure = slackDisclosure({
    conversationTypes: settings.conversationTypes,
    followUpHours: settings.followUpHours,
    retentionDays: settings.retentionDays,
    ai,
    sweep: settings.sweep,
    sweepMeetingDays: settings.sweepMeetingDays,
    climate,
  });
  return (
    <>
      {lead}
      <ul data-slack-disclosure="">
        <li><strong>What Calis may read:</strong> {disclosure.reads}.</li>
        <li><strong>How a check looks:</strong> {disclosure.how}.</li>
        <li><strong>When:</strong> {disclosure.when}.</li>
        <li><strong>What leaves Calis:</strong> {disclosure.leaves}.</li>
        <li><strong>What Calis keeps:</strong> {slackKeptSentence(disclosure.kept, climate)}</li>
      </ul>
      <p>
        Choose the channels Calis won&rsquo;t read, and switch direct messages and group chats or automatic checks off, under{' '}
        <Link to={PATHS.connections}>Connections</Link>.
      </p>
    </>
  );
}
